import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { SharedModule, NotificationService, FormInputComponent, StringCustomFieldConfig } from '@vendure/admin-ui/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { DEFAULT_POLICIES } from '../../default-policies-constant';
import { PolicyContentData, PolicyTemplate } from '../../ui-types';
import { safeJSONParse } from '../../utils';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';

@Component({
  selector: 'vdr-store-policy-templates',
  templateUrl: './store-policy-templates.component.html',
  styleUrls: ['./store-policy-templates.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedModule],
})
export class StorePolicyTemplatesComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, OnDestroy, AfterViewInit {
  readonly = false;
  formControl: FormControl;

  private _config: StringCustomFieldConfig;
  get config(): StringCustomFieldConfig {
    return this._config;
  }
  // Called whenever the dynamic form re-binds the config/def (e.g. on channel change)
  set config(config: StringCustomFieldConfig) {
    this._config = config;
    this.storeSiteData = config.ui?.options?.storeSiteData || null;
    this.updateStoreMetaFromStoreSiteData();
    if (this.formControl) {
      this.closeEditor(false);
    }
  }

  storeSiteData: any;
  policyContentList: PolicyContentData[] = [];
  policyForm: FormGroup;
  private subscriptions: Subscription[] = [];
  editingPolicy = new BehaviorSubject<PolicyTemplate | null>(null);
  editingPolicy$: Observable<PolicyTemplate | null> = this.editingPolicy.asObservable();
  private policyTemplateMap = new Map<string, PolicyTemplate>(
    DEFAULT_POLICIES.map(template => [template.type, template]),
  );
  editingContentControl = new FormControl('');

  storeName: string = '';
  storeUrl: string = '';
  storeEmail: string = '';
  supportEmail: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private notificationService: NotificationService,
  ) { }

  ngOnInit(): void {
    this.storeSiteData = this.config.ui?.options?.storeSiteData || null;
    this.updateStoreMetaFromStoreSiteData();

    this.initForm();
    const defaultPolicyContentData: PolicyContentData = {
      type: '',
      title: '',
      path: '',
      content: '',
    };
    this.readPoliciesFromControl(this.formControl.value, defaultPolicyContentData);
    this.populateFormFromExistingPolicies();
    this.markFormAsPristine();

    // subscribe to the enabled control of each policy
    this.getPolicyFormGroups().forEach(policyControl => {
      const enabledControl = policyControl.get('enabled');
      if (enabledControl) {
        const sub = enabledControl.valueChanges.subscribe((enabled: boolean) => {
          this.closeEditor();
          const policyType = policyControl.get('type')?.value;
          const template = policyType ? this.policyTemplateMap.get(policyType) : undefined;
          if (template) {
            // When enabled, set default content; when disabled, clear content.
            if (enabled) {
              const defaultContent = this.replacePlaceholders(template.defaultContent);
              policyControl.get('content')?.setValue(defaultContent);
            } else {
              policyControl.get('content')?.setValue('');
            }
            this.updateFormControl();
          }
        });
        this.subscriptions.push(sub);
      }
    });
  }

  /** Update local meta fields from the current storeSiteData */
  private updateStoreMetaFromStoreSiteData() {
    this.storeName = this.storeSiteData?.name || '';
    this.storeUrl = this.storeSiteData?.storeUrl || '';
    this.storeEmail = this.storeSiteData?.sellerChannel?.customFields?.notificationEmail || '';
    this.supportEmail = this.storeSiteData?.supportEmail || '';
  }

  /** Parse formControl value (array of JSON strings) into policyContentList */
  private readPoliciesFromControl(raw: unknown, fallback: PolicyContentData) {
    this.policyContentList = [];
    try {
      const existingPolicies = raw || '[]';
      if (Array.isArray(existingPolicies)) {
        if (existingPolicies.length === 0) {
          this.updateFormControl();
        } else {
          this.policyContentList =
            existingPolicies
              .map((policy: unknown) => {
                //TODO: remove after migrating/republish all old data
                if (typeof policy === 'string') {
                  return safeJSONParse<PolicyContentData>(policy, this.notificationService, fallback);
                }
                return policy as PolicyContentData;
              })
              .filter(notNullOrUndefined) ?? [];
        }
      }
    } catch (e) {
      console.error('Error parsing policy content from form control:', e);
    }
  }

  private markFormAsPristine(): void {
    this.getPolicyFormGroups().forEach(control => {
      control.markAsPristine();
      Object.keys(control.controls).forEach(key => {
        control.get(key)?.markAsPristine();
      });
    });
    this.policiesFormArray.markAsPristine();
    this.policyForm.markAsPristine();
    this.editingContentControl.markAsPristine();
    if (this.formControl) {
      this.formControl.markAsPristine();
    }
  }

  ngAfterViewInit() {
    if (this.formControl) {
      // When user edits the rich-text content
      const editSub = this.editingContentControl.valueChanges.subscribe(() => {
        const editingPolicy = this.editingPolicy.getValue();
        if (editingPolicy) {
          const policyControl = this.findPolicyControl(editingPolicy.type);
          if (policyControl) {
            const updatedContent = this.editingContentControl.value;
            policyControl.get('content')?.setValue(updatedContent);
            this.updateFormControl();
          }
        }
      });
      this.subscriptions.push(editSub);

      // Sync when the parent formControl is updated externally (e.g. channel switch)
      const defaultPolicyContentData: PolicyContentData = {
        type: '',
        title: '',
        path: '',
        content: '',
      };
      const valueSub = this.formControl.valueChanges.subscribe(value => {
        this.closeEditor(false);
        this.readPoliciesFromControl(value, defaultPolicyContentData);
        this.populateFormFromExistingPolicies();
        this.markFormAsPristine();
      });
      this.subscriptions.push(valueSub);

      // Update parent when the internal form changes (but only after init)
      const formSub = this.policyForm.valueChanges.subscribe(() => {
          this.updateFormControl();
      });
      this.subscriptions.push(formSub);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private createPoliciesArray(): FormArray {
    this.policyTemplateMap = new Map<string, PolicyTemplate>(
      DEFAULT_POLICIES.map(template => [template.type, template]),
    );
    const policiesArray = this.formBuilder.array<FormGroup>([]);

    for (const template of this.policyTemplateMap.values()) {
      policiesArray.push(
        this.formBuilder.group({
          type: [template.type],
          name: [template.name],
          enabled: [template.required ? true : false],
          content: [this.replacePlaceholders(template.defaultContent)],
          required: [template.required],
        }),
      );
    }

    return policiesArray;
  }

  private initForm(): void {
    this.policyForm = this.formBuilder.group({
      policies: this.createPoliciesArray(),
    });
  }

  private populateFormFromExistingPolicies(): void {
    // First, handle required policies regardless of existing values
    this.getPolicyFormGroups().forEach(policyControl => {
      const isRequired = policyControl.get('required')?.value === true;

      if (isRequired) {
        policyControl.get('enabled')?.setValue(true, { emitEvent: false });
        policyControl.get('enabled')?.disable({ emitEvent: false });
      }
      else {
        policyControl.get('enabled')?.setValue(false,{ emitEvent: false });
        policyControl.get('enabled')?.enable({ emitEvent: false });
      }
      const template = this.policyTemplateMap.get(policyControl.get('type')?.value);
      if (template) {
        const defaultContent = this.replacePlaceholders(template.defaultContent);
        policyControl.get('content')?.setValue(defaultContent,{ emitEvent: false });
      }else {
        console.error('Template not found for policy', policyControl.get('type')?.value);
        policyControl.get('content')?.setValue('',{ emitEvent: false });
      }
    });

    if (!this.policyContentList || !this.policyContentList.length) {
      return;
    }

    // Parse existing policies and update form values
    this.policyContentList.forEach(policy => {
      try {
        if (policy && policy.type) {
          // Map policy type to template type
          const templateType = policy.type;
          const policyControl = this.findPolicyControl(templateType);

          if (policyControl) {
            const isRequired = policyControl.get('required')?.value === true;

            // Set to enabled (if not already required)
            if (!isRequired) {
              policyControl.get('enabled')?.setValue(true,{ emitEvent: false });
            }

            // Always update content
            policyControl.get('content')?.setValue(policy.content || '',{ emitEvent: false });
          }
        }
      } catch (e) {
        console.error('Error parsing policy JSON:', e);
      }
    });
  }

  private findPolicyControl(type: string): FormGroup | null {
    for (let i = 0; i < this.policiesFormArray.length; i++) {
      const control = this.policiesFormArray.at(i) as FormGroup;
      if (control.get('type')?.value === type) {
        return control;
      }
    }
    return null;
  }

  get policiesFormArray(): FormArray {
    const control = this.policyForm?.get('policies') as FormArray | null | undefined;
    // Explicit cast avoids the FormArray<never> inference
    return (control ?? this.formBuilder.array([])) as FormArray;
  }

  isPolicySelected(policy: FormGroup): boolean {
    const editing = this.editingPolicy.getValue();
    return editing ? editing.type === policy.get('type')?.value : false;
  }

  getPolicyFormGroups(): FormGroup[] {
    return this.policiesFormArray.controls as FormGroup[];
  }

  editPolicy(policy: FormGroup): void {
    this.closeEditor();
    const policyType = policy.get('type')?.value;
    const template = this.policyTemplateMap.get(policyType);

    if (template) {
      const currentContent = policy.get('content')?.value || '';
      this.editingContentControl.setValue(currentContent);
      const policyToEdit = {
        ...template,
        enabled: policy.get('enabled')?.value,
        content: currentContent
      };
      this.editingPolicy.next(policyToEdit);
    }
  }

  closeEditor(commit: boolean = true): void {
    const editingPolicy = this.editingPolicy.getValue();
    if (editingPolicy) {
      if (commit) {
        const policyControl = this.findPolicyControl(editingPolicy.type);
        if (policyControl) {
          const updatedContent = this.editingContentControl.value;
          policyControl.get('content')?.setValue(updatedContent, { emitEvent: false });
          this.updateFormControl();
        }
      }
      this.editingPolicy.next(null);
      this.editingContentControl.setValue('', { emitEvent: false });
    }
  }

  resetToDefault(policyType: string): void {
    const template = this.policyTemplateMap.get(policyType);
    const policyControl = this.findPolicyControl(policyType);
    if (template && policyControl) {
      const defaultContent = this.replacePlaceholders(template.defaultContent);
      policyControl.get('content')?.setValue(defaultContent, { emitEvent: false });
      this.updateFormControl();
      this.editingContentControl.setValue('', { emitEvent: false });
      this.editingContentControl.setValue(defaultContent);
    }
  }

  replacePlaceholders(content: string): string {
    if (!content) return '';

    return content
      .replace(/\{\{storeName\}\}/g, this.storeName || '[Store Name]')
      .replace(/\{\{storeURL\}\}/g, this.storeUrl || '[Store URL]')
      .replace(/\{\{storeEmail\}\}/g, this.storeEmail || '[Store Email]')
      .replace(/\{\{supportEmail\}\}/g, this.supportEmail || '[Support Email]');
  }

  getEnabledPolicies(): any[] {
    return this.getPolicyFormGroups()
      .filter(policy => policy.get('enabled')?.value && !policy.get('future')?.value)
      .map(policy => {
        const policyData = policy.value;
        const policyType = policyData.type;
        const template = this.policyTemplateMap.get(policyData.type);
        return {
          type: policyType,
          title: policyData.name,
          path: template?.slug,
          content: policyData.content
        };
      });
  }

  getPoliciesAsJsonList(): string[] {
    return this.getEnabledPolicies().map(policy => JSON.stringify(policy));
  }

  private updateFormControl(): void {
    if (this.formControl) {
      const policiesList = this.getPoliciesAsJsonList();
      this.formControl.setValue(policiesList, { emitEvent: false });
      this.formControl.markAsDirty();
    }
  }
}
